import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Badge, Button, Dropdown, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Sample } from '../../services/sample.service';
import { getSampleListRowTone } from '../../utils/sampleUsageRecords';
import { canManageSample } from '../../utils/samplePermissions';
import {
  type CaseLocationCell,
  formatCasePanelLocationHeader,
} from '../../utils/caseLocationUtils';
import {
  navigateToSampleDetail,
  navigateToSampleListWithLocationCell,
} from '../../utils/samplePageNavigation';
import { compareNaturalStrings } from '../../utils/naturalStringCompare';
import {
  formatEntityDisplayName,
  softBreakIdentifier,
} from '../../utils/softBreakIdentifier';
import { formatSampleTypeDisplay } from '../../utils/sampleTypeLabels';
import { joinLabeledValue } from '../../utils/joinLabeledValue';
import { formatSampleQuantityWithUnit } from '../../utils/sampleUnitLabels';
import { bslLevelToneClass } from '../../utils/sampleBslDisplay';
import { syncCaseSampleTableColgroup } from '../../utils/syncDataTableColgroup';
import CaseSampleRowActions from './CaseSampleRowActions';

interface CaseSampleListPanelProps {
  selected: CaseLocationCell | null;
  roomName?: string | null;
  samples: Sample[];
  isLoading: boolean;
  /**
   * 左ツリーのセル件数。読込中に samples が空でもヘッダが「0件」にならないよう使う。
   * フィルター適用中は一覧件数とずれるためヘッダには使わない。
   */
  indexSampleCount?: number | null;
  /** 利用可のみ表示中に隠している残なし件数 */
  hiddenOutOfStockCount?: number;
  /** 利用可のみ表示中に隠している廃棄済み件数 */
  hiddenDisposedCount?: number;
  /** 残なし／廃棄の非表示分を表示（種別で status を切替、または全件） */
  onRevealHiddenStatus?: (mode: CaseHiddenStatusRevealMode) => void;
  /** 利用可能のみ以外のとき、既定フィルターへ戻す */
  onRestoreAvailableFilter?: () => void;
  showRestoreAvailableFilter?: boolean;
  currentUserId?: number | null;
  isAdmin?: boolean;
  adminViewAll?: boolean;
  onDetail: (sample: Sample) => void;
  onEdit: (sample: Sample) => void;
  onDelete: (sample: Sample) => void;
  onAddUsage: (sample: Sample) => void;
  onCreateSample: () => void;
  /** モバイル master-detail: ケース選択へ戻る */
  onChangeCase?: () => void;
  /** ロケーション行の下・一覧の上に差し込む（フィルターなど） */
  filterSlot?: ReactNode;
  /** ヘッダ操作列（戻る／共有のあいだ。xs ヘルプなど） */
  headerTools?: ReactNode;
  /** 検索・詳細フィルター適用中（空表示の文言を分ける） */
  hasActiveFilters?: boolean;
  /** フィルター空のときの解除（親の setFilters({}) 等） */
  onClearFilters?: () => void;
  /** 選択中ロケーションのゲスト共有（保管庫割当ありのみ） */
  onShareLocation?: (scope: 'case' | 'drawer' | 'room') => void;
  canShareLocation?: boolean;
}

type SortField = 'name' | 'sampleType' | 'remainingQuantity' | 'bslLevel' | 'status';
type SortDirection = 'asc' | 'desc';

function sampleStatusRank(sample: Sample): number {
  const tone = getSampleListRowTone(sample);
  if (tone === 'disposed') return 2;
  if (tone === 'depleted') return 1;
  return 0;
}

function compareSamples(
  a: Sample,
  b: Sample,
  field: SortField,
  direction: SortDirection
): number {
  let cmp = 0;
  switch (field) {
    case 'name':
      cmp = compareNaturalStrings(a.name, b.name);
      break;
    case 'sampleType':
      cmp = compareNaturalStrings(a.sampleType, b.sampleType);
      break;
    case 'remainingQuantity':
      cmp = (a.remainingQuantity ?? -1) - (b.remainingQuantity ?? -1);
      break;
    case 'bslLevel':
      cmp = compareNaturalStrings(a.bslLevel, b.bslLevel);
      break;
    case 'status':
      cmp = sampleStatusRank(a) - sampleStatusRank(b);
      break;
    default:
      cmp = 0;
  }
  return direction === 'asc' ? cmp : -cmp;
}

export type CaseHiddenStatusRevealMode = 'disposed' | 'outOfStock' | 'all';

/** 利用可能のみ表示時の「隠れた件数」示唆ラベルと開示モード */
export function resolveCaseHiddenStatusHint(
  outOfStock: number,
  disposed: number
): {
  count: number;
  shortKey: string;
  fullKey: string;
  mode: CaseHiddenStatusRevealMode;
} | null {
  const oos = Math.max(0, outOfStock);
  const disp = Math.max(0, disposed);
  const count = oos + disp;
  if (count <= 0) return null;
  if (disp > 0 && oos === 0) {
    return {
      count: disp,
      shortKey: 'sample.case_manager.show_disposed_hint_short',
      fullKey: 'sample.case_manager.show_disposed_hint',
      mode: 'disposed',
    };
  }
  if (oos > 0 && disp === 0) {
    return {
      count: oos,
      shortKey: 'sample.case_manager.show_out_of_stock_hint_short',
      fullKey: 'sample.case_manager.show_out_of_stock_hint',
      mode: 'outOfStock',
    };
  }
  return {
    count,
    shortKey: 'sample.case_manager.show_hidden_status_hint_short',
    fullKey: 'sample.case_manager.show_hidden_status_hint',
    mode: 'all',
  };
}

function SortableHeader({
  label,
  fullLabel,
  field,
  activeField,
  direction,
  onSort,
  className,
}: {
  label: string;
  /** 短ラベル時の title / aria（省略時は label） */
  fullLabel?: string;
  field: SortField;
  activeField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const isActive = activeField === field;
  const icon = isActive
    ? direction === 'asc'
      ? 'fa-sort-up'
      : 'fa-sort-down'
    : 'fa-sort';
  const columnName = fullLabel ?? label;
  const sortTitle = t('common.sort_by', {
    column: columnName,
    defaultValue: `Sort by ${columnName}`,
  });
  return (
    <th
      className={className}
      aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold border-0"
        onClick={() => onSort(field)}
        aria-label={label}
        title={sortTitle}
      >
        {label}
        <i className={`fas ${icon} ms-1 text-muted`} aria-hidden="true" />
      </button>
    </th>
  );
}

export default function CaseSampleListPanel({
  selected,
  roomName,
  samples,
  isLoading,
  indexSampleCount = null,
  hiddenOutOfStockCount = 0,
  hiddenDisposedCount = 0,
  onRevealHiddenStatus,
  onRestoreAvailableFilter,
  showRestoreAvailableFilter = false,
  currentUserId,
  isAdmin = false,
  adminViewAll = false,
  onDetail,
  onEdit,
  onDelete,
  onAddUsage,
  onCreateSample,
  onChangeCase,
  filterSlot,
  headerTools,
  hasActiveFilters = false,
  onClearFilters,
  onShareLocation,
  canShareLocation = false,
}: CaseSampleListPanelProps) {
  const { t } = useTranslation();
  const openInListFull = t('sample.case_manager.open_in_sample_list');
  const openInListShort = t('sample.case_manager.open_in_sample_list_short', {
    defaultValue: openInListFull,
  });
  const createInCaseFull = t('sample.case_manager.new_sample_in_case');
  const createInCaseShort = t('sample.case_manager.new_sample_in_case_short', {
    defaultValue: createInCaseFull,
  });
  const navigate = useNavigate();
  const tableRef = useRef<HTMLTableElement>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedSamples = useMemo(() => {
    const copy = [...samples];
    copy.sort((a, b) => compareSamples(a, b, sortField, sortDirection));
    return copy;
  }, [samples, sortField, sortDirection]);

  const showOwnerColumn = useMemo(
    () => samples.some((s) => !canManageSample(s, currentUserId, { isAdmin, adminViewAll })),
    [samples, currentUserId, isAdmin, adminViewAll]
  );

  useLayoutEffect(() => {
    if (!selected) return;
    const table = tableRef.current;
    if (!table) return;
    const sync = () => syncCaseSampleTableColgroup(table);
    sync();
    const host = table.parentElement ?? table;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    ro?.observe(host);
    window.addEventListener('resize', sync);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [selected, sortedSamples, showOwnerColumn, sortField, sortDirection]);

  if (!selected) {
    return (
      <div className="text-center text-muted py-3 px-2">
        <i className="fas fa-box-open fa-lg mb-1 d-block opacity-50" aria-hidden="true" />
        <p className="mb-0 small">{t('sample.case_manager.select_case_prompt')}</p>
      </div>
    );
  }

  const locationFull = formatCasePanelLocationHeader(selected, t, { roomName });
  // xs master-detail ではツリーが隠れるため保管庫名を見出しに残す
  const locationCompact = formatCasePanelLocationHeader(selected, t, {
    roomName,
    compact: true,
    includeRoom: Boolean(onChangeCase),
  });
  const headerCount =
    isLoading &&
    samples.length === 0 &&
    !hasActiveFilters &&
    typeof indexSampleCount === 'number'
      ? indexSampleCount
      : samples.length;
  const hiddenStatusHint = resolveCaseHiddenStatusHint(
    hiddenOutOfStockCount,
    hiddenDisposedCount
  );
  const hiddenHintShort = hiddenStatusHint
    ? t(hiddenStatusHint.shortKey, {
        count: hiddenStatusHint.count,
        defaultValue: `+他 ${hiddenStatusHint.count}`,
      })
    : null;
  const hiddenHintFull = hiddenStatusHint
    ? t(hiddenStatusHint.fullKey, {
        count: hiddenStatusHint.count,
        defaultValue: `非表示 ${hiddenStatusHint.count} 件を表示`,
      })
    : null;
  const restoreAvailableShort = t('sample.case_manager.restore_available_filter_short', {
    defaultValue: '利用可のみ',
  });
  const restoreAvailableFull = t('sample.case_manager.restore_available_filter', {
    defaultValue: '利用可のみに戻す',
  });

  return (
    <div id="case-sample-list-panel">
      <div className="case-sample-list-header d-flex flex-wrap align-items-center gap-2 mb-2">
        <div className="case-sample-list-header-lead d-flex flex-nowrap align-items-center gap-2 min-w-0 flex-grow-1">
          {onChangeCase ? (
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-md-none sample-chrome-icon-btn flex-shrink-0"
              onClick={onChangeCase}
              title={t('sample.case_manager.back_to_tree', { defaultValue: 'Back to cases' })}
              aria-label={t('sample.case_manager.back_to_tree_short', {
                defaultValue: t('common.back'),
              })}
            >
              <i className="fas fa-arrow-left" aria-hidden="true" />
            </Button>
          ) : null}
          <div className="min-w-0 flex-grow-1 overflow-hidden" title={locationFull}>
            {/* xs: ケース名優先（truncate 耐性）。md+: ツリー順（引き出し → ケース） */}
            <div className="fw-semibold small case-sample-list-location d-md-none">
              {locationCompact}
            </div>
            <div className="fw-semibold small case-sample-list-location d-none d-md-block">
              {locationFull}
            </div>
            <div className="text-muted case-sample-list-count d-flex flex-wrap align-items-center gap-1">
              {isLoading && samples.length === 0 && hasActiveFilters ? (
                <span className="visually-hidden">{t('common.loading')}</span>
              ) : (
                <span className="text-truncate">
                  {t('sample.case_manager.sample_count', { count: headerCount })}
                </span>
              )}
              {!isLoading && hiddenStatusHint && onRevealHiddenStatus && hiddenHintShort ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => onRevealHiddenStatus(hiddenStatusHint.mode)}
                  aria-label={hiddenHintShort}
                  title={hiddenHintFull ?? undefined}
                >
                  {hiddenHintShort}
                </button>
              ) : null}
              {!isLoading && showRestoreAvailableFilter && onRestoreAvailableFilter ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={onRestoreAvailableFilter}
                  aria-label={restoreAvailableShort}
                  title={restoreAvailableFull}
                >
                  {restoreAvailableShort}
                </button>
              ) : null}
              {/* ヘルプ等は件数行へ。1行目の場所名 truncate を優先する */}
              {headerTools}
            </div>
          </div>
        </div>
        <div className="case-sample-list-header-actions d-flex flex-nowrap align-items-center gap-2 flex-shrink-0 ms-md-0">
          {canShareLocation && onShareLocation ? (
            <Dropdown className="flex-shrink-0">
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                id="case-sample-share-toggle"
                className="sample-chrome-icon-btn case-sample-share-toggle"
                title={t('sample.location_share_menu', { defaultValue: '場所を共有' })}
                aria-label={t('sample.location_share_menu_short', { defaultValue: '共有' })}
              >
                <i className="fas fa-share-alt" aria-hidden="true" />
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                {(
                  [
                    [
                      'case',
                      'sample.location_share_case_short',
                      'ケース',
                      'sample.location_share_case',
                      'このケースを共有',
                    ],
                    [
                      'drawer',
                      'sample.location_share_drawer_short',
                      '引き出し',
                      'sample.location_share_drawer',
                      'この引き出しを共有',
                    ],
                    [
                      'room',
                      'sample.location_share_room_short',
                      '保管庫',
                      'sample.location_share_room',
                      'この保管庫を共有',
                    ],
                  ] as const
                ).map(([scope, shortKey, shortFallback, fullKey, fullFallback]) => {
                  const shortLabel = t(shortKey, { defaultValue: shortFallback });
                  const fullLabel = t(fullKey, { defaultValue: fullFallback });
                  return (
                    <Dropdown.Item
                      key={scope}
                      onClick={() => onShareLocation(scope)}
                      title={fullLabel}
                      aria-label={shortLabel}
                    >
                      {shortLabel}
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            className="sample-chrome-icon-btn flex-shrink-0"
            onClick={onCreateSample}
            title={createInCaseFull}
            aria-label={createInCaseShort}
          >
            <i className="fas fa-plus" aria-hidden="true" />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            className="sample-chrome-icon-btn flex-shrink-0"
            onClick={() => navigateToSampleListWithLocationCell(navigate, selected)}
            title={openInListFull}
            aria-label={openInListShort}
          >
            <i className="fas fa-list" aria-hidden="true" />
            <span className="d-none d-md-inline ms-1">{openInListShort}</span>
          </Button>
        </div>
      </div>

      {/* 一覧画面と同様、検索は常時表示（モバイルで虫眼鏡トグルに隠さない） */}
      {filterSlot ? <div className="mb-2">{filterSlot}</div> : null}

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      ) : samples.length === 0 ? (
        <div className="text-center text-muted py-4 px-3 border rounded bg-light">
          <p className="mb-3 mb-md-2">
            {hasActiveFilters
              ? t('sample.case_manager.empty_filtered', {
                  defaultValue: 'No samples match the current filters',
                })
              : t('sample.case_manager.empty_case')}
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-2">
            {hasActiveFilters && onClearFilters ? (
              <Button
                variant="primary"
                size="sm"
                onClick={onClearFilters}
                aria-label={t('common.clear')}
                title={t('sample.clear_filters', { defaultValue: 'Clear filters' })}
              >
                <i className="fas fa-times me-1" aria-hidden="true" />
                {t('common.clear')}
              </Button>
            ) : null}
            {!hasActiveFilters ? (
              <Button
                variant="primary"
                size="sm"
                onClick={onCreateSample}
                title={createInCaseFull}
                aria-label={createInCaseShort}
              >
                <i className="fas fa-plus me-1" aria-hidden="true" />
                {createInCaseShort}
              </Button>
            ) : null}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigateToSampleListWithLocationCell(navigate, selected)}
              title={openInListFull}
              aria-label={openInListShort}
            >
              <i className="fas fa-list me-1" aria-hidden="true" />
              {openInListShort}
            </Button>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <Table
            ref={tableRef}
            striped
            bordered
            hover
            size="sm"
            className="mb-0 case-sample-table"
          >
            <thead>
              <tr>
                <SortableHeader
                  className="case-sample-name-col"
                  label={t('sample.sample_name')}
                  field="name"
                  activeField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  className="case-sample-type-col"
                  label={t('sample.sample_type')}
                  field="sampleType"
                  activeField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  className="case-sample-qty-col"
                  label={t('sample.remaining_quantity_short', { defaultValue: 'Rem' })}
                  fullLabel={t('sample.remaining_quantity')}
                  field="remainingQuantity"
                  activeField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  className="case-sample-bsl-col"
                  label={t('sample.bsl_level_short', { defaultValue: 'BSL' })}
                  fullLabel={t('sample.bsl_level')}
                  field="bslLevel"
                  activeField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  className="case-sample-status-col"
                  label={t('sample_filter.status')}
                  field="status"
                  activeField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                {showOwnerColumn && (
                  <th className="case-sample-owner-col">{t('storage_room.owner_name')}</th>
                )}
                <th className="case-sample-actions-col">
                  <span className="visually-hidden">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedSamples.map((sample) => {
                const canManage = canManageSample(sample, currentUserId, {
                  isAdmin,
                  adminViewAll,
                });
                const openDetail = () =>
                  navigateToSampleDetail(navigate, sample.id, {
                    preferAdminViewAll: adminViewAll,
                    returnPath: '/samples/cases',
                  });
                const sampleDisplayName = formatEntityDisplayName(sample.name, t);
                const quickDetailLabel = t('sample.open_quick_detail');
                const quickDetailHint = t('sample.open_quick_detail_hint', {
                  defaultValue: quickDetailLabel,
                });
                const readOnlyTitle = t('sample.case_manager.read_only_other_user');
                const manageDisabledTitle = !canManage ? readOnlyTitle : undefined;
                const ownerLabel =
                  sample.creatorDisplayName ||
                  (sample.user
                    ? [sample.user.lastName, sample.user.firstName].filter(Boolean).join(' ')
                    : '') ||
                  '-';
                const statusBadge = sample.disposalDate ? (
                  <Badge bg="secondary">{t('sample_filter.status_disposed')}</Badge>
                ) : sample.remainingQuantity === 0 ? (
                  <Badge bg="warning" text="dark">
                    {t('sample_filter.status_out_of_stock')}
                  </Badge>
                ) : (
                  <Badge bg="success">{t('sample_filter.status_available')}</Badge>
                );
                const showMobileStatus =
                  Boolean(sample.disposalDate) || sample.remainingQuantity === 0;
                return (
                  <tr key={sample.id}>
                    <td className="case-sample-name-col">
                      <div className="case-sample-name-block">
                        <Button
                          variant="link"
                          className="p-0 text-start text-decoration-none case-sample-name-btn"
                          onClick={() => onDetail(sample)}
                          aria-label={sampleDisplayName}
                          title={[quickDetailHint, sampleDisplayName, sample.bslLevel]
                            .filter(Boolean)
                            .join(' · ')}
                        >
                          <span className="case-sample-name-text">
                            {softBreakIdentifier(sample.name, t)}
                          </span>
                        </Button>
                        <span className="case-sample-mobile-qty">
                          {joinLabeledValue(
                            t('sample.remaining_quantity_short', {
                              defaultValue: '残',
                            }),
                            formatSampleQuantityWithUnit(
                              sample.remainingQuantity,
                              sample.unit,
                              t
                            )
                          )}
                        </span>
                      </div>
                      {showMobileStatus ? (
                        <div className="case-sample-mobile-status">{statusBadge}</div>
                      ) : null}
                    </td>
                    <td className="case-sample-type-col text-nowrap">
                      {formatSampleTypeDisplay(sample.sampleType, t)}
                    </td>
                    <td className="case-sample-qty-col text-end">
                      {formatSampleQuantityWithUnit(
                        sample.remainingQuantity,
                        sample.unit,
                        t
                      )}
                    </td>
                    <td className="case-sample-bsl-col text-nowrap">
                      {sample.bslLevel ? (
                        <span className={bslLevelToneClass(sample.bslLevel)} title={sample.bslLevel}>
                          {sample.bslLevel}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="case-sample-status-col">{statusBadge}</td>
                    {showOwnerColumn && (
                      <td className="case-sample-owner-col">{ownerLabel}</td>
                    )}
                    <td className="case-sample-actions-col">
                      <CaseSampleRowActions
                        sample={sample}
                        canManage={canManage}
                        manageDisabledTitle={manageDisabledTitle}
                        onDetail={openDetail}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onAddUsage={onAddUsage}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}
